/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, Container, Flex, Button, ThemeUICSSObject } from "theme-ui";
import { keyframes } from "@emotion/react";
import { Link } from "components/link";
import { Link as ScrollToLink } from "react-scroll";
import Logo from "components/logo";
import { DrawerProvider } from "../../contexts/drawer/drawer.provider";
import MobileDrawer from "./mobile-drawer";

// @ts-ignore
import LogoDark from "assets/logo-dark.svg";
// @ts-ignore
import LogoWhite from "assets/logo.svg";
import { useContext } from "react";
import { ModalContext } from "components/signup-modal";
import { logEvent } from "analytics";

export default function Header({
  className,
  menuItems,
  includeNavs,
  position,
}: {
  className: string;
  menuItems: { path: string; label: string }[];
  includeNavs: boolean;
  position?: "static" | "relative" | "absolute" | "sticky" | "fixed";
}) {
  const { toggle } = useContext(ModalContext);

  return (
    <DrawerProvider>
      <header
        sx={
          {
            ...styles.header,
            // Only keep animation to fixed if we are sticky already.
            ...(position === "sticky" ? {} : { animation: undefined }),
          } as ThemeUICSSObject
        }
        className={className}
        id="header"
        style={{ position }}
      >
        <Container sx={styles.container}>
          <Logo src={className === "sticky" ? LogoDark : LogoWhite} />

          <Flex as="nav" sx={styles.nav}>
            {includeNavs &&
              menuItems.map(({ path, label }, i) => (
                <ScrollToLink
                  activeClass="active"
                  to={path}
                  spy={true}
                  smooth={true}
                  offset={-70}
                  duration={500}
                  key={i}
                >
                  {label}
                </ScrollToLink>
              ))}
          </Flex>

          <Link path="/app/programs">
            <Button
              className="donate__btn"
              variant="secondary"
              // aria-label="Get Early Access"
              aria-label="Watch Lessons Now"
              onClick={() => {
                logEvent("Button", "watch_lessons_now_header");
                // toggle();
              }}
            >
              Watch Lessons Now
              {/* Get Early Access */}
            </Button>
          </Link>

          {menuItems.length > 0 && <MobileDrawer menuItems={menuItems} />}
        </Container>
      </header>
    </DrawerProvider>
  );
}

const positionAnim = keyframes`
  from {
    position: fixed;
    opacity: 1;
  }
  to {
    position: absolute;
    opacity: 1;
    transition: all 0.4s ease;
  }
`;

const styles = {
  header: {
    color: "white",
    fontWeight: "normal",
    py: 4,
    width: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    backgroundColor: "transparent",
    transition: "all 0.5s ease",
    animation: `${positionAnim} 0.4s ease`,
    ".donate__btn": {
      flexShrink: 0,
      mr: [15, 20, null, null, 0],
      ml: ["auto", null, null, null, 0],
    },
    "&.sticky": {
      position: "fixed",
      backgroundColor: "background",
      color: "text",
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.06)",
      py: 3,
      "nev > a": {
        color: "text",
      },
      ".donate__btn": {
        borderColor: "primary",
        color: "primary",
        "&:hover": {
          boxShadow: "rgba(31, 62, 118, 0.57) 0px 9px 20px -5px",
          backgroundColor: "primary",
          color: "white",
        },
      },
    },
  },
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  nav: {
    mx: "auto",
    display: "none",
    "@media screen and (min-width: 1024px)": {
      display: "block",
    },
    a: {
      fontSize: "16px",
      fontWeight: "400",
      px: 25,
      cursor: "pointer",
      lineHeight: "1.2",
      "&.active": {
        color: "secondary",
      },
    },
  },
};
